/**
 * Woofers Phase 0 — Owner Settings → AI Assistant.
 *
 * The assistant's name (what customers and staff call it, e.g. "Woofers")
 * and one switch per AI surface the platform has today:
 *   - staff chat (the AI Manager page) — on by default;
 *   - customer chat (replies in the customer app) — OFF by default (#526):
 *     customers get AI replies only once an owner turns this on.
 *
 *   - texting (Woofers Phase 2b) — answering texts clients send to the
 *     business number — OFF by default. It only takes effect once texting
 *     is turned on for the business (`features.sms`, after the number's
 *     carrier approval); the description says so, so the switch is never a
 *     silent dead end (Principle 2).
 *
 * Read for business-config:read:all; write business-config:write:all
 * (owner, manager). The switches stay the owner's (#933 D2): a manager names
 * the assistant and reads the switches as statements. The PUT is a PATCH —
 * the card sends only what changed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Checkbox, Input, Skeleton } from '@ferro/ui';
import {
  adminTenantSettingsApi,
  type TenantAssistantKey,
  type TenantAssistantPatch,
  type TenantAssistantSettings,
} from '../../lib/api.js';

interface AssistantSettingsCardProps {
  apiUrl: string;
  /** Holds business-config:write:all (owner, manager). Everyone else sees read-only. */
  canEdit: boolean;
  /** #933 D2 — may turn an AI surface on or off (owner). Defaults to `canEdit`. */
  canEditSwitches?: boolean;
}

type Status = 'loading' | 'ready' | 'error';

interface FormState {
  name: string;
  staffChatEnabled: boolean;
  customerChatEnabled: boolean;
  textingEnabled: boolean;
}

/** Mirrors the server's rule so the owner hears it before the round trip. */
const NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} .'&-]*$/u;

const KEY_LABELS: Record<TenantAssistantKey, string> = {
  name: 'the name',
  staffChatEnabled: 'staff chat',
  customerChatEnabled: 'customer chat',
  textingEnabled: 'texting',
};

function formFromSettings(s: TenantAssistantSettings): FormState {
  return {
    name: s.assistant.name ?? '',
    staffChatEnabled: s.assistant.staffChatEnabled,
    customerChatEnabled: s.assistant.customerChatEnabled,
    textingEnabled: s.assistant.textingEnabled,
  };
}

function patchFromForm(form: FormState, s: TenantAssistantSettings): TenantAssistantPatch {
  const patch: TenantAssistantPatch = {};
  const name = form.name.trim();
  if (name !== (s.assistant.name ?? '')) patch.name = name === '' ? null : name;
  if (form.staffChatEnabled !== s.assistant.staffChatEnabled) patch.staffChatEnabled = form.staffChatEnabled;
  if (form.customerChatEnabled !== s.assistant.customerChatEnabled) {
    patch.customerChatEnabled = form.customerChatEnabled;
  }
  if (form.textingEnabled !== s.assistant.textingEnabled) patch.textingEnabled = form.textingEnabled;
  return patch;
}

function validateName(name: string, max: number): string | null {
  const trimmed = name.trim();
  if (trimmed === '') return null;
  if (trimmed.length > max) return `Keep the name to ${max} characters or fewer.`;
  if (!NAME_PATTERN.test(trimmed)) return "Use letters, numbers, spaces and . ' & - only.";
  return null;
}

function humanList(keys: ReadonlyArray<TenantAssistantKey>): string {
  const labels = keys.map((k) => KEY_LABELS[k]);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
}

export function AssistantSettingsCard({
  apiUrl,
  canEdit,
  canEditSwitches = canEdit,
}: AssistantSettingsCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [settings, setSettings] = useState<TenantAssistantSettings | null>(null);
  const [form, setForm] = useState<FormState>({ name: '', staffChatEnabled: true, customerChatEnabled: false, textingEnabled: false });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ changedKeys: TenantAssistantKey[] } | null>(null);

  const apply = useCallback((s: TenantAssistantSettings) => {
    setSettings(s);
    setForm(formFromSettings(s));
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    setLoadError(null);
    setSaved(null);
    const res = await adminTenantSettingsApi.getAssistant(apiUrl);
    if (res.error || !res.data) {
      setLoadError(res.error ?? 'Could not load the AI assistant settings.');
      setStatus('error');
      return;
    }
    apply(res.data);
    setStatus('ready');
  }, [apiUrl, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty = useMemo(
    () => (settings ? Object.keys(patchFromForm(form, settings)).length > 0 : false),
    [form, settings],
  );

  const handleSave = useCallback(async () => {
    if (!settings) return;
    setSaveError(null);
    setSaved(null);
    const invalid = validateName(form.name, settings.limits.maxNameLength);
    if (invalid) {
      setSaveError(invalid);
      return;
    }
    const body = patchFromForm(form, settings);
    if (Object.keys(body).length === 0) return;
    setIsSaving(true);
    const res = await adminTenantSettingsApi.updateAssistant(apiUrl, body);
    setIsSaving(false);
    if (res.error || !res.data) {
      setSaveError(res.error ?? 'Could not save. Please try again.');
      return;
    }
    const { changedKeys, audited: _audited, ...view } = res.data;
    apply(view);
    setSaved({ changedKeys });
  }, [apiUrl, apply, form, settings]);

  const handleReset = useCallback(() => {
    if (settings) setForm(formFromSettings(settings));
    setSaveError(null);
    setSaved(null);
  }, [settings]);

  if (status === 'loading') {
    return (
      <Card title="AI Assistant">
        <div data-testid="assistant-settings-loading">
          <Skeleton variant="text" lines={4} />
        </div>
      </Card>
    );
  }

  if (status === 'error' || !settings) {
    return (
      <Card title="AI Assistant">
        <div data-testid="assistant-settings-error">
          <Alert variant="error">{loadError ?? 'Could not load the AI assistant settings.'}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const { assistant, source, limits, lastChange } = settings;
  const callIt = form.name.trim() || assistant.name || 'the assistant';
  const defaultMark = (key: TenantAssistantKey): JSX.Element | null =>
    source[key] === 'default' ? (
      <span data-testid={`assistant-default-${key}`}>
        <Badge variant="platform">Platform default</Badge>
      </span>
    ) : null;

  return (
    <Card
      title="AI Assistant"
      subtitle="Name your assistant and choose where it helps. Each switch takes effect on the next message."
    >
      <div className="ferro-settings__card" data-testid="assistant-settings-ready">
        {lastChange ? (
          <p className="ferro-settings__last-change" data-testid="assistant-last-change">
            Last changed by <strong>{lastChange.byName}</strong>
            {lastChange.at ? ` on ${whenLabel(lastChange.at)}` : ''}
            {lastChange.changedKeys.length > 0 ? ` — ${humanList(lastChange.changedKeys)}` : ''}
          </p>
        ) : null}

        {canEdit ? (
          <form
            className="ferro-form ferro-settings__form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void handleSave();
            }}
            aria-label="AI assistant settings"
          >
            <div className="ferro-settings__field">
              <Input
                name="assistantName"
                label="Assistant name"
                value={form.name}
                maxLength={limits.maxNameLength}
                placeholder="Woofers"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                helperText="What your customers and staff call it. Leave blank to keep it unnamed."
                data-testid="assistant-name"
              />
              <div className="ferro-settings__field-meta">{defaultMark('name')}</div>
            </div>

            {canEditSwitches ? (
              <>
                <div className="ferro-settings__field">
                  <Checkbox
                    name="staffChatEnabled"
                    label={`Staff can chat with ${callIt}`}
                    description="On the AI Manager page: today's schedule, customers, dogs, vaccinations, and booking from the chat."
                    checked={form.staffChatEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, staffChatEnabled: e.target.checked }))}
                    data-testid="assistant-staff-chat"
                  />
                  <div className="ferro-settings__field-meta">{defaultMark('staffChatEnabled')}</div>
                </div>

                <div className="ferro-settings__field">
                  <Checkbox
                    name="customerChatEnabled"
                    label={`${callIt[0]?.toUpperCase() ?? ''}${callIt.slice(1)} answers customers in the app`}
                    description="Replies to messages customers send in the customer app: their bookings, how their pet is doing, services and prices. It does not book, change or cancel; it hands those to your staff. Off means every message waits for your team."
                    checked={form.customerChatEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, customerChatEnabled: e.target.checked }))}
                    data-testid="assistant-customer-chat"
                  />
                  <div className="ferro-settings__field-meta">{defaultMark('customerChatEnabled')}</div>
                </div>

                <div className="ferro-settings__field">
                  <Checkbox
                    name="textingEnabled"
                    label={`${callIt[0]?.toUpperCase() ?? ''}${callIt.slice(1)} answers texts to your business number`}
                    description="Replies to texts from your clients: their bookings, how their pet is doing, services and prices. Anything it can't handle goes to your team, and you get one short text at the alert number in Settings → Texting. Texts go out only once texting is turned on for your business; until then they wait in the Inbox."
                    checked={form.textingEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, textingEnabled: e.target.checked }))}
                    data-testid="assistant-texting"
                  />
                  <div className="ferro-settings__field-meta">
                    {defaultMark('textingEnabled')}
                    <Link to="/settings/messaging" data-testid="assistant-texting-link">
                      See what clients get and what&apos;s left to go live
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <div data-testid="assistant-switches-readonly">
                <dl className="ferro-settings__readonly">
                  <div className="ferro-settings__readonly-row">
                    <dt>Staff chat</dt>
                    <dd>{assistant.staffChatEnabled ? 'On' : 'Off'}</dd>
                  </div>
                  <div className="ferro-settings__readonly-row">
                    <dt>Answers customers in the app</dt>
                    <dd>{assistant.customerChatEnabled ? 'On' : 'Off'}</dd>
                  </div>
                  <div className="ferro-settings__readonly-row">
                    <dt>Answers texts to your business number</dt>
                    <dd>{assistant.textingEnabled ? 'On' : 'Off'}</dd>
                  </div>
                </dl>
                <p className="ferro-muted">Only the owner can turn these on or off.</p>
              </div>
            )}

            {saveError ? (
              <div data-testid="assistant-save-error">
                <Alert variant="error">{saveError}</Alert>
              </div>
            ) : null}
            {saved && !saveError ? (
              <div data-testid="assistant-save-success">
                <Alert variant="success">
                  {saved.changedKeys.length === 0
                    ? 'Saved. Nothing changed.'
                    : `Saved. Changed ${humanList(saved.changedKeys)}.`}
                </Alert>
              </div>
            ) : null}

            <div className="ferro-settings__actions">
              <Button type="submit" variant="primary" isLoading={isSaving} disabled={!isDirty || isSaving} data-testid="assistant-save">
                Save
              </Button>
              <Button type="button" variant="ghost" onClick={handleReset} disabled={!isDirty || isSaving} data-testid="assistant-reset">
                Reset
              </Button>
            </div>
          </form>
        ) : (
          <dl className="ferro-settings__readonly" data-testid="assistant-settings-readonly">
            <div className="ferro-settings__readonly-row">
              <dt>Assistant name</dt>
              <dd>{assistant.name ?? 'Not named'}</dd>
            </div>
            <div className="ferro-settings__readonly-row">
              <dt>Staff chat</dt>
              <dd>{assistant.staffChatEnabled ? 'On' : 'Off'}</dd>
            </div>
            <div className="ferro-settings__readonly-row">
              <dt>Answers customers in the app</dt>
              <dd>{assistant.customerChatEnabled ? 'On' : 'Off'}</dd>
            </div>
          </dl>
        )}
      </div>
    </Card>
  );
}
